import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Divider, 
  LinearProgress,
  Tooltip,
  Card,
  CardContent
} from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import StarHalfIcon from '@material-ui/icons/StarHalf';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import PeopleIcon from '@material-ui/icons/People';
import DevicesIcon from '@material-ui/icons/Devices';
import AssessmentIcon from '@material-ui/icons/Assessment';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '& svg': {
      marginRight: theme.spacing(1),
    },
  },
  sectionContent: {
    padding: theme.spacing(3),
  },
  ratingBar: {
    height: '24px',
    borderRadius: '12px',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
    position: 'relative',
    backgroundColor: theme.palette.grey[200],
    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
  },
  ratingFill: {
    height: '100%',
    borderRadius: '12px',
    transition: 'width 1s ease-in-out',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: theme.spacing(1),
    fontWeight: 'bold',
    color: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  ratingLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  scoreCard: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
  },
  statValue: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  statLabel: {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  stars: {
    display: 'flex',
    marginTop: theme.spacing(1),
    color: '#FFD700',
    '& svg': {
      fontSize: '1.8rem',
    },
  },
  rangeIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  summaryBox: {
    padding: theme.spacing(2),
    backgroundColor: '#e3f2fd',
    borderLeft: '6px solid #1976d2',
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3),
  },
  insightBox: {
    padding: theme.spacing(2),
    backgroundColor: '#f1f8e9',
    borderLeft: '6px solid #43a047',
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  ratingCount: {
    position: 'absolute',
    right: theme.spacing(1),
    color: 'white',
    fontWeight: 'bold',
    fontSize: '0.9rem',
  },
  noDataMessage: {
    padding: theme.spacing(4),
    textAlign: 'center',
  }
}));

// Helper to generate star rating display
const RatingStars = ({ rating, maxRating = 5 }) => {
  const classes = useStyles();
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  
  for (let i = 1; i <= maxRating; i++) {
    if (i <= fullStars) {
      stars.push(<StarIcon key={i} />);
    } else if (i === fullStars + 1 && hasHalfStar) {
      stars.push(<StarHalfIcon key={i} />);
    } else {
      stars.push(<StarBorderIcon key={i} />);
    }
  }
  
  return <Box className={classes.stars}>{stars}</Box>;
};

const ScaleVisualization = ({ data = {}, metrics = {} }) => {
  const classes = useStyles();
  const [animateIn, setAnimateIn] = useState(false);
  
  // Extract scale metrics safely
  const averageRating = metrics?.averageRating || metrics?.simple_score || 0;
  const minScore = metrics?.minScore || metrics?.min_score || 1;
  const maxScore = metrics?.maxScore || metrics?.max_score || 5;
  const median = metrics?.median || 0;
  const mode = metrics?.mode || 0;
  const totalResponses = metrics?.totalResponses || 0;
  const responseRate = metrics?.participationRate || 0;
  
  // Safe access to user segments and platforms
  const userTypes = metrics?.userTypes || {};
  const platformUsage = metrics?.platformUsage || {};
  
  // Create distribution data from summary if available
  const createDistributionData = () => {
    // Check if we have actual distribution data
    if (Array.isArray(metrics?.distribution) && metrics.distribution.length > 0) {
      return metrics.distribution.map(item => ({
        rating: item.value || item.rating || 0,
        count: item.count || 0,
        percentage: item.percentage || 0
      }));
    }
    
    // Create mock distribution based on available data
    const allScores = [];
    for (let i = minScore; i <= maxScore; i++) {
      // Calculate percentage based on averageRating (higher percent closer to average)
      const distanceFromAvg = Math.abs(i - averageRating);
      const percentage = Math.max(5, 100 - (distanceFromAvg * 25));
      
      allScores.push({
        rating: i,
        count: Math.floor((percentage / 100) * totalResponses),
        percentage: percentage
      });
    }
    
    return allScores.sort((a, b) => a.rating - b.rating);
  };
  
  const distributionData = createDistributionData();
  
  // Animation effect on load
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateIn(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);
  
  // Generate a rating color based on the score
  const getRatingColor = (rating) => {
    const ratio = (rating - minScore) / (maxScore - minScore);
    if (ratio < 0.33) return '#f44336'; // Red for low scores
    if (ratio < 0.66) return '#ff9800'; // Orange for medium scores
    return '#4caf50'; // Green for high scores
  };
  
  // Helper to format numbers with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  
  // If there's no data, display a message
  if (totalResponses === 0) {
    return (
      <Paper className={classes.noDataMessage}>
        <Typography variant="h6" color="textSecondary" gutterBottom>
          Rating Data Unavailable
        </Typography>
        <Typography variant="body2" color="textSecondary">
          There is insufficient data to display ratings. This may be because no responses have been recorded yet.
        </Typography>
      </Paper>
    );
  }
  
  return (
    <Box className={classes.root}>
      {/* Summary Box */}
      <Box className={classes.summaryBox}>
        <Typography variant="h6" gutterBottom color="primary">
          Rating Summary
        </Typography>
        <Typography variant="body1">
          This scale question received an average rating of <strong>{averageRating.toFixed(1)}</strong> out of {maxScore}.
          The response rate was {responseRate}% with {totalResponses} total responses.
        </Typography>
      </Box>
      
      {/* Key Metrics */}
      <Grid container spacing={3} style={{ marginBottom: theme => theme.spacing(3) }}>
        <Grid item xs={12} sm={4}>
          <Card className={classes.scoreCard}>
            <Typography className={classes.statValue}>{averageRating.toFixed(1)}</Typography>
            <Typography className={classes.statLabel}>Average Rating</Typography>
            <RatingStars rating={averageRating} maxRating={maxScore} />
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <Card className={classes.scoreCard}>
            <Typography className={classes.statValue}>{median || '-'}</Typography>
            <Typography className={classes.statLabel}>Median Score</Typography>
            {median > 0 && <RatingStars rating={median} maxRating={maxScore} />}
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <Card className={classes.scoreCard}>
            <Typography className={classes.statValue}>{mode || '-'}</Typography>
            <Typography className={classes.statLabel}>Most Common Score</Typography>
            {mode > 0 && <RatingStars rating={mode} maxRating={maxScore} />}
          </Card>
        </Grid>
      </Grid>
      
      {/* Score Distribution */}
      <Paper className={classes.section}>
        <Box className={classes.sectionHeader}>
          <AssessmentIcon />
          <Typography variant="h6">Rating Distribution</Typography>
        </Box>
        
        <Box className={classes.sectionContent}>
          <Box className={classes.rangeIndicator}>
            <Typography variant="body2">
              <strong>Scale Range:</strong> {minScore} - {maxScore}
            </Typography>
            <Typography variant="body2">
              <strong>Total Responses:</strong> {formatNumber(totalResponses)}
            </Typography>
          </Box>
          
          {distributionData.map((item) => (
            <Box key={item.rating}>
              <Box className={classes.ratingLabel}>
                <Typography variant="body2">Rating: {item.rating}</Typography>
                <Typography variant="body2">{Math.round(item.percentage)}%</Typography>
              </Box>
              <Box className={classes.ratingBar}>
                <Box 
                  className={classes.ratingFill}
                  style={{ 
                    width: animateIn ? `${Math.max(5, item.percentage)}%` : '0%',
                    backgroundColor: getRatingColor(item.rating)
                  }}
                >
                  {item.count > 0 && (
                    <span className={classes.ratingCount}>{item.count}</span>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>
      
      {/* User Engagement */}
      {Object.values(userTypes).some(v => v > 0) && (
        <Paper className={classes.section}>
          <Box className={classes.sectionHeader}>
            <PeopleIcon />
            <Typography variant="h6">User Engagement</Typography>
          </Box>
          
          <Box className={classes.sectionContent}>
            <Grid container spacing={2}>
              {Object.entries(userTypes)
                .filter(([_, value]) => value > 0)
                .map(([key, value]) => (
                  <Grid item xs={6} sm={3} key={key}>
                    <Tooltip title={`${key.replace(/_/g, ' ').toUpperCase()}`}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:
                        </Typography>
                        <Typography variant="h6">{value}</Typography>
                      </Box>
                    </Tooltip>
                  </Grid>
                ))}
            </Grid>
          </Box>
        </Paper>
      )}
      
      {/* Platform Distribution */}
      {Object.values(platformUsage).some(v => v > 0) && (
        <Paper className={classes.section}>
          <Box className={classes.sectionHeader}>
            <DevicesIcon />
            <Typography variant="h6">Platform Distribution</Typography>
          </Box>
          
          <Box className={classes.sectionContent}>
            <Grid container spacing={2}>
              {Object.entries(platformUsage)
                .filter(([_, value]) => value > 0)
                .map(([key, value]) => (
                  <Grid item xs={6} sm={3} key={key}>
                    <Typography variant="body2" color="textSecondary">
                      {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:
                    </Typography>
                    <Typography variant="h6">{value}</Typography>
                  </Grid>
                ))}
            </Grid>
          </Box>
        </Paper>
      )}
      
      {/* Insight Box */}
      <Box className={classes.insightBox}>
        <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#2e7d32' }} gutterBottom>
          Key Insight
        </Typography>
        <Typography variant="body2">
          {averageRating > (maxScore * 0.7) 
            ? "Users indicate strong satisfaction with the POPins, suggesting they find significant value in the tool."
            : averageRating > (maxScore * 0.4)
            ? "Users show moderate satisfaction with POPins, indicating potential for improvement in value delivery."
            : "Users report low satisfaction, suggesting immediate action to improve the POPin value proposition."}
        </Typography>
      </Box>
    </Box>
  );
};

export default ScaleVisualization; 